import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import gql from 'graphql-tag';

import { Observable, of } from 'rxjs';
import { CrmGraphql } from '../../../shared/constants/graphql-constants';
import {
  dateSubstring,
  getDateFromString,
  getMMDDYYYFormat,
} from '../../../shared/utilities/date-utilities';

const batchProcessQuery = gql`
  query(
    $fileStatus: String
    $fileType: String
    $dealerName: String
    $dealerNumber: String
    $fileName: String
    $uploadDateFrom: String
    $uploadDateTo: String
    $planCount: Int
    $contractRange: String
    $dealerCost: String
    $nlfTransactionDateFrom: Date
    $nlfTransactionDateTo: Date
    $cancellationTransactionDateFrom: Date
    $cancellationTransactionDateTo: Date
    $offset: Int
    $limit: Int
  ) {
    getBatchProcessSearchResults(
      fileStatus: $fileStatus
      fileType: $fileType
      dealerName: $dealerName
      dealerNumber: $dealerNumber
      fileName: $fileName
      uploadDateFrom: $uploadDateFrom
      uploadDateTo: $uploadDateTo
      planCount: $planCount
      contractRange: $contractRange
      dealerCost: $dealerCost
      nlfTransactionDateFrom: $nlfTransactionDateFrom
      nlfTransactionDateTo: $nlfTransactionDateTo
      cancellationTransactionDateFrom: $cancellationTransactionDateFrom
      cancellationTransactionDateTo: $cancellationTransactionDateTo
      offset: $offset
      limit: $limit
    ) {
      batchProcessingIdOriginal
      fileStatus
      fileType
      dealerName
      dealerNumber
      fileName
      uploadDate
      planCount
      contractRange
      dealerCost
      extendedDealerCost
      nlfTransactionDate
      operationResults
      cancellationTransactionDate
    }
  }
`;

const createUpdateBatchProcessQuery = gql`
  mutation(
    $batchProcessingIdOriginal: Int
    $fileStatus: String
    $fileType: String
    $dealerName: String
    $dealerNumber: String
    $uploadDate: Date
    $fileName: String
    $planCount: Int
    $contractRange: String
    $dealerCost: String
    $extendedDealerCost: String
    $nlfTransactionDate: String
    $cancellationTransactionDate: String
    $insertUserName: String
    $hasStatusChanged: Boolean
    $hasNLFDateChanged: Boolean
    $hasCancellationDateChanged: Boolean
    $operationType: String
    $comments: String
  ) {
    createUpdateBatchProcess(
      batchProcess: {
        batchProcessingIdOriginal: $batchProcessingIdOriginal
        fileStatus: $fileStatus
        fileType: $fileType
        dealerName: $dealerName
        dealerNumber: $dealerNumber
        uploadDate: $uploadDate
        fileName: $fileName
        planCount: $planCount
        contractRange: $contractRange
        dealerCost: $dealerCost
        extendedDealerCost: $extendedDealerCost
        nlfTransactionDate: $nlfTransactionDate
        cancellationTransactionDate: $cancellationTransactionDate
        hasStatusChanged: $hasStatusChanged
        hasNLFDateChanged: $hasNLFDateChanged
        hasCancellationDateChanged: $hasCancellationDateChanged
        insertUserName: $insertUserName
        operationType: $operationType
        comments: $comments
      }
    ) {
      batchProcessingIdOriginal
    }
  }
`;

@Injectable({
  providedIn: 'root'
})
export class BatchProcessingService {
  constructor(private apollo: Apollo) {}
  getBatchProcessing(): Observable<any[]> {
    return of([
      {
        fileStatus: 'COMPLETE',
        fileType: 's',
        dealerName: 'Matrix',
        fileName: 'batch-processing-22072020.csv',
        uploadDate: '12/06/2020',
        planCount: '-1654',
        contractRange: 'NL-2344834-3485309302',
        dealerCost: '-5.0',
        extendedDealerCost: '-123',
        nlfTransactionDate: '',
        cancellationTransactionDate: '12/06/2020'
      },
      {
        fileStatus: 'COMPLETE',
        fileType: 's',
        dealerName: 'Matrix',
        fileName: 'batch-processing-22072020.csv',
        uploadDate: '12/06/2020',
        planCount: '1654',
        contractRange: 'NL-2344834-3485309302',
        dealerCost: '5.0',
        extendedDealerCost: '-123',
        nlfTransactionDate: 'UNPROCESSED',
        cancellationTransactionDate: ''
      },
      {
        fileStatus: 'COMPLETE',
        fileType: 's',
        dealerName: 'Matrix',
        fileName: 'batch-processing-22072020.csv',
        uploadDate: '12/06/2020',
        planCount: '1654',
        contractRange: 'NL-2344834-3485309302',
        dealerCost: '5.0',
        extendedDealerCost: '123',
        nlfTransactionDate: '06/16/2020',
        cancellationTransactionDate: 'UNPROCESSED'
      }
    ]);
  }

  getBatchProcessSearchResults = data => {
    const v = data.dealerNumber;
    const dealerNumberVal = v != null && String(v).trim() !== '' ? String(v).trim() : null;

    return this.apollo.use(CrmGraphql.QueryNameSpace).watchQuery<any>({
      query: batchProcessQuery,
      variables: {
        ...data,
        fileStatus: BatchProcessingService.resolveFileStatus(data.fileStatus),
        planCount: BatchProcessingService.resolveIntSearchValue(data.planCount),
        dealerNumber: dealerNumberVal,
        uploadDateFrom: BatchProcessingService.formatUploadDateSearchValue(data.uploadDateFrom),
        uploadDateTo: BatchProcessingService.formatUploadDateSearchValue(data.uploadDateTo),
        nlfTransactionDateFrom: BatchProcessingService.formatGraphqlDateSearchValue(
          data.nlfTransactionDateFrom,
        ),
        nlfTransactionDateTo: BatchProcessingService.formatGraphqlDateSearchValue(
          data.nlfTransactionDateTo,
        ),
        cancellationTransactionDateFrom: BatchProcessingService.formatGraphqlDateSearchValue(
          data.cancellationTransactionDateFrom,
        ),
        cancellationTransactionDateTo: BatchProcessingService.formatGraphqlDateSearchValue(
          data.cancellationTransactionDateTo,
        ),
      },
      fetchPolicy: 'network-only'
      // tslint:disable-next-line: semicolon
    }).valueChanges;
  };

  private static resolveFileStatus(value: any): string | null | undefined {
    if (value == null || value === '') {
      return null;
    }
    return value.value;
  }

  /** Backend upload-date filters expect MM/DD/YYYY strings. */
  private static formatUploadDateSearchValue(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return getMMDDYYYFormat(value, 'MM/DD/YYYY');
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }
    if (trimmed.includes('/')) {
      return trimmed;
    }
    if (/^\d{4}-\d{2}-\d{2}/.test(trimmed)) {
      return getDateFromString(trimmed.includes('T') ? trimmed : `${trimmed}T00:00:00`);
    }

    return trimmed;
  }

  /** GraphQL Date variables expect YYYY-MM-DD (or null when unset). */
  private static formatGraphqlDateSearchValue(value: unknown): string | null {
    if (value == null || value === '') {
      return null;
    }
    if (value instanceof Date) {
      return dateSubstring(value.toISOString());
    }

    const trimmed = String(value).trim();
    if (!trimmed) {
      return null;
    }

    return dateSubstring(trimmed);
  }

  createUpdateBatchProcess = data =>
    this.apollo.use(CrmGraphql.NameSpace).mutate({
      mutation: createUpdateBatchProcessQuery,
      variables: {
        ...data,
        batchProcessingIdOriginal: BatchProcessingService.resolveIntSearchValue(
          data.batchProcessingIdOriginal,
        ),
        planCount: BatchProcessingService.resolveIntSearchValue(data.planCount),
      }
      // tslint:disable-next-line: semicolon
    });

  /** GraphQL Int variables must be numbers, not form textbox strings. */
  private static resolveIntSearchValue(value: unknown): number | null {
    if (value == null || value === '') {
      return null;
    }

    const parsed = parseInt(String(value).trim(), 10);
    return Number.isNaN(parsed) ? null : parsed;
  }
}
